import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { ApiClient } from './api/client.js';
import {
  createAuthClient,
  REFRESH_MARGIN_SECONDS,
  type AuthClient,
  type Session,
} from './auth/cognito.js';
import type { RuntimeConfig } from './config.js';
import { Telemetry, browserTransport } from './telemetry.js';

/**
 * The session, the API client and the telemetry, in one provider (M1.14.3).
 *
 * They are together because they are one lifecycle: the client needs a token,
 * the token needs refreshing, and a refresh that fails ends the session — so
 * splitting them into three providers would mean three of them holding
 * references to each other.
 *
 * ## Tokens live where `amazon-cognito-identity-js` puts them
 *
 * Which is `localStorage`. That is a real trade and worth stating plainly: a
 * script running on this origin could read them. The alternatives are worse
 * for what this product promises — in-memory only means signing in again on
 * every reload and no 90-day session ([D7](../../Design-Decisions.md)), and a
 * cookie-based session needs a server-side exchange the architecture does not
 * have.
 *
 * What makes it defensible is that **feed HTML is sanitised server-side before
 * it is ever stored** (M1.2.7), and the archive — the genuinely untrusted
 * markup — is served from a *separate origin* by design ([D4](../../Design-Decisions.md)),
 * so it cannot reach this one's storage. The residual risk is a bug in our
 * own sanitiser, which is exactly where the corpus tests are pointed.
 *
 * ## Refresh happens ahead of expiry, not after a failure
 *
 * A timer refreshes five minutes before the hour is up. The 401 path in the
 * API client stays as the backstop — a laptop that slept through the timer
 * wakes with a dead token — but an application that only ever refreshes
 * *after* a failure shows the user a failure.
 */

export interface SessionState {
  readonly status: 'loading' | 'signed-in' | 'signed-out';
  readonly email: string | undefined;
  /** A stable key for per-account storage. The email, which is what we have. */
  readonly userKey: string;
}

export interface SessionValue extends SessionState {
  readonly auth: AuthClient;
  readonly api: ApiClient;
  readonly telemetry: Telemetry;
  readonly config: RuntimeConfig;
  signIn(email: string, password: string): Promise<void>;
  signOut(): void;
  /** After a sign-up confirmation, to pick the session up without a reload. */
  refresh(): Promise<void>;
}

const SessionContext = createContext<SessionValue | undefined>(undefined);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === undefined) {
    throw new Error('useSession must be used inside <SessionProvider>');
  }
  return value;
}

export function SessionProvider({
  config,
  children,
  authClient,
}: {
  config: RuntimeConfig;
  children: ReactNode;
  /** Injected by tests; the real one talks to Cognito. */
  authClient?: AuthClient;
}): ReactNode {
  const auth = useMemo(
    () => authClient ?? createAuthClient(config),
    [authClient, config],
  );

  const [state, setState] = useState<SessionState>({
    status: 'loading',
    email: undefined,
    userKey: 'anonymous',
  });

  const sessionRef = useRef<Session | undefined>(undefined);

  const apply = useCallback((session: Session | undefined) => {
    sessionRef.current = session;
    setState(
      session === undefined
        ? { status: 'signed-out', email: undefined, userKey: 'anonymous' }
        : { status: 'signed-in', email: session.email, userKey: session.email },
    );
  }, []);

  /** A valid token, refreshed if it is close to expiry. */
  const getToken = useCallback(async (): Promise<string | undefined> => {
    const current = sessionRef.current;
    const now = Math.floor(Date.now() / 1000);

    if (current !== undefined && current.expiresAt - now > REFRESH_MARGIN_SECONDS) {
      return current.idToken;
    }

    const session = await auth.currentSession();
    apply(session);
    return session?.idToken;
  }, [auth, apply]);

  const refreshToken = useCallback(async (): Promise<string | undefined> => {
    const session = await auth.currentSession({ forceRefresh: true });
    apply(session);
    return session?.idToken;
  }, [auth, apply]);

  const api = useMemo(
    () =>
      new ApiClient({
        baseUrl: config.apiBaseUrl,
        getToken,
        refreshToken,
        onSignedOut: () => {
          apply(undefined);
        },
      }),
    [config.apiBaseUrl, getToken, refreshToken, apply],
  );

  const telemetry = useMemo(
    () =>
      new Telemetry({
        send: browserTransport((events) => api.reportEvents([...events])),
      }),
    [api],
  );

  /* ---- pick up an existing session on load ---- */

  useEffect(() => {
    let cancelled = false;

    void auth.currentSession().then((session) => {
      if (!cancelled) {
        apply(session);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [auth, apply]);

  /* ---- refresh ahead of expiry, not after a failure ---- */

  useEffect(() => {
    if (state.status !== 'signed-in') {
      return;
    }

    const timer = setInterval(
      () => {
        void getToken();
      },
      // Well inside the hour the token lasts, and cheap: the call only talks
      // to Cognito when the margin has actually been reached.
      60_000,
    );

    return () => {
      clearInterval(timer);
    };
  }, [state.status, getToken]);

  /* ---- session instrumentation (H1) ---- */

  useEffect(() => {
    if (state.status !== 'signed-in') {
      return;
    }

    telemetry.sessionStart();

    const end = (): void => {
      telemetry.sessionEnd();
    };

    /*
     * `pagehide`, not `unload`: mobile Safari does not fire `unload` at all,
     * so the end of every mobile session would be missing — which would bias
     * the one measurement that can end the project.
     */
    window.addEventListener('pagehide', end);

    return () => {
      window.removeEventListener('pagehide', end);
      end();
    };
  }, [state.status, telemetry]);

  const value = useMemo<SessionValue>(
    () => ({
      ...state,
      auth,
      api,
      telemetry,
      config,
      async signIn(email, password) {
        apply(await auth.signIn(email, password));
      },
      signOut() {
        auth.signOut();
        apply(undefined);
      },
      async refresh() {
        apply(await auth.currentSession({ forceRefresh: true }));
      },
    }),
    [state, auth, api, telemetry, config, apply],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
